import { useState } from "react";
import { X, Send, Bell, BellOff, MessageSquare, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { useNotificationFeed, useNotificationStatus } from "../hooks/useNotificationStatus";
import { getAuthToken } from "../lib/api";

interface NotificationFeedProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationFeed({ isOpen, onClose }: NotificationFeedProps) {
  const { notifications, loading, refresh } = useNotificationFeed(20);
  const { status } = useNotificationStatus();
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  if (!isOpen) return null;

  async function sendTest() {
    setSending(true);
    setSendResult(null);
    try {
      const token = getAuthToken();
      const res = await fetch("/api/notifications/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: "custom",
          message: "🧪 <b>Test desde Mission Control</b>\n\nConexión verificada.",
        }),
      });
      const data = await res.json();
      setSendResult(data.ok ? "Enviado" : `Error: ${data.error}`);
      refresh();
    } catch (err) {
      setSendResult("Error de conexión");
    } finally {
      setSending(false);
      setTimeout(() => setSendResult(null), 3000);
    }
  }

  function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "ahora";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  function statusIcon(s: string) {
    switch (s) {
      case "sent":
        return <CheckCircle className="w-3 h-3 text-emerald-400" />;
      case "failed":
        return <XCircle className="w-3 h-3 text-red-400" />;
      case "skipped":
        return <AlertTriangle className="w-3 h-3 text-yellow-400" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  }

  function stripHtml(text: string) {
    return text.replace(/<[^>]*>/g, "").slice(0, 120);
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-surface-1 border-l border-border shadow-2xl flex flex-col animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-accent" />
            <span className="text-sm font-semibold text-gray-200">Telegram Notifications</span>
            {status && (
              <span className="text-[10px] text-gray-500">
                {status.totalSent24h} hoy
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={sendTest}
              disabled={sending || !status?.botConnected}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors disabled:opacity-50"
            >
              <Send className="w-3 h-3" />
              {sending ? "..." : "Test"}
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Send result toast */}
        {sendResult && (
          <div className={`px-4 py-2 text-xs ${sendResult.startsWith("Error") ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}`}>
            {sendResult}
          </div>
        )}

        {/* Feed */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="text-sm text-gray-500">Cargando...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <BellOff className="w-8 h-8 mb-3 opacity-50" />
              <p className="text-sm">Sin notificaciones</p>
              <p className="text-xs mt-1">Las notificaciones enviadas a Telegram apareceran aqui.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => (
                <div key={n.id} className="px-4 py-3 hover:bg-surface-4 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    {statusIcon(n.status)}
                    <span
                      className="text-[11px] font-medium"
                      style={{ color: n.project_color || "#9ca3af" }}
                    >
                      {n.agent_name}
                    </span>
                    {n.project_name && (
                      <span className="text-[10px] text-gray-600">/ {n.project_name}</span>
                    )}
                    <span className="ml-auto text-[10px] text-gray-600">
                      {timeAgo(n.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {stripHtml(n.message)}
                  </p>
                  {n.error_message && (
                    <p className="text-[10px] text-red-400 mt-1">{n.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with stats */}
        {status && (
          <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-[10px] text-gray-500">
            <span>{status.activeSchedules} schedules activos</span>
            {status.lastSent && <span>Ultimo: {timeAgo(status.lastSent)}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// Telegram indicator for the header
export function TelegramIndicator() {
  const [feedOpen, setFeedOpen] = useState(false);
  const { status, loading } = useNotificationStatus();

  if (loading) return null;

  const connected = status?.botConnected ?? false;

  return (
    <>
      <button
        onClick={() => setFeedOpen(true)}
        className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full transition-colors hover:bg-surface-4"
        title={connected ? `@${status?.botUsername} conectado` : "Telegram desconectado"}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-cyan-400 animate-pulse-dot" : "bg-red-400"}`}
        />
        <Bell className={`w-3 h-3 ${connected ? "text-cyan-400" : "text-red-400"}`} />
        {status && status.totalSent24h > 0 && (
          <span className="text-gray-400">{status.totalSent24h}</span>
        )}
      </button>

      <NotificationFeed isOpen={feedOpen} onClose={() => setFeedOpen(false)} />
    </>
  );
}
